import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Heading,
  Container,
  Spacer,
  Button,
  Icon,
  Flex,
  Box,
  VStack,
  SimpleGrid,
  Text,
  Badge,
  Tabs,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useParams, NavLink } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  getDay,
  subMonths,
  addMonths,
} from 'date-fns';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import EventDrawer from './event-drawer';
import EventModal from './event-modal';
import { Event } from '@/lib/types';
import api from '@/utils/api';

export default function Calendar() {
  const { workspaceId, calendarType } = useParams<{
    workspaceId: string;
    calendarType: string;
  }>();
  const [eventModalState, setEventModalState] = useState({
    isOpen: false,
    eventId: null as number | null,
    selectedDate: null as string | null,
  });
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<string>(
    format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  );
  const [endDate, setEndDate] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const itemDrawer = useModalToggle();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const currentUser = useIsCurrentUser();
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();

  const handleView = (item: Event) => {
    setReferenceId(item.reference_id);
    itemDrawer.open();
  };

  const fetchData = useCallback(async () => {
    if (!workspaceId || !currentUser?.id) {
      return;
    }

    setIsLoading(true);
    try {
      const status = calendarType === 'my-calendar' ? 'all' : 'published';
      const createdBy = calendarType === 'my-calendar' ? currentUser.id : 0;

      const response = await api.get(
        `/calendars/workspace/${workspaceId}/created-by/${createdBy}/${startDate}/${endDate}/${status}`,
      );
      setEvents(response.data || []);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to get events',
      });
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, calendarType, currentUser, startDate, endDate]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDay = getDay(monthStart);

    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const emptyStartCells = Array(startDay).fill(null);

    return [...emptyStartCells, ...days];
  }, [currentMonth]);

  const getEventsForDay = useCallback(
    (day: Date | null) => {
      if (!day) return [];

      const calendarDayString = format(day, 'yyyy-MM-dd');

      return events.filter((event) => {
        if (!event.event_date) return false;

        const eventDateString = event.event_date.substring(0, 10);

        return eventDateString === calendarDayString;
      });
    },
    [events],
  );

  const handleMonthChange = useCallback(
    (direction: 'prev' | 'next') => {
      const newMonth =
        direction === 'prev' ? subMonths(currentMonth, 1) : addMonths(currentMonth, 1);

      setCurrentMonth(newMonth);
      setStartDate(format(startOfMonth(newMonth), 'yyyy-MM-dd'));
      setEndDate(format(endOfMonth(newMonth), 'yyyy-MM-dd'));
    },
    [currentMonth],
  );

  const handleCreateEvent = (selectedDate?: Date) => {
    setEventModalState({
      isOpen: true,
      eventId: null,
      selectedDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
    });
  };

  const handleEditEvent = (eventId: number) => {
    setEventModalState({ isOpen: true, eventId, selectedDate: null });
  };

  const closeEventModal = () => {
    setEventModalState({ isOpen: false, eventId: null, selectedDate: null });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEventSuccess = () => {
    fetchData();
  };

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header>
        <Container>
          <Flex mb={4}>
            <Tabs.Root
              value={calendarType}
              orientation={isSmallScreen ? 'vertical' : 'horizontal'}
              variant={isSmallScreen ? 'enclosed' : 'line'}
              size='lg'
            >
              <Tabs.List width={isSmallScreen ? 'full' : 'auto'}>
                <Tabs.Trigger value='workspace-calendar' asChild>
                  <Button colorPalette='brand' variant='ghost' asChild>
                    <NavLink to={`/${workspaceId}/calendar/workspace-calendar`}>
                      Workspace Calendar
                    </NavLink>
                  </Button>
                </Tabs.Trigger>
                {isAdmin && (
                  <Tabs.Trigger value='my-calendar' asChild>
                    <Button colorPalette='brand' variant='ghost' asChild>
                      <NavLink to={`/${workspaceId}/calendar/my-calendar`}>My Calendar</NavLink>
                    </Button>
                  </Tabs.Trigger>
                )}
              </Tabs.List>
            </Tabs.Root>
            <Spacer />
            {isAdmin && !isCancelled && (
              <Button colorPalette='brand' onClick={() => handleCreateEvent()}>
                <Icon as={PlusIcon} w={4} h={4} />
                Create
              </Button>
            )}
          </Flex>
        </Container>
      </Card.Header>
      <Card.Body>
        <Container>
          <Flex
            mb={6}
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'start', md: 'center' }}
            gap={4}
          >
            <Flex gap={1} w='full'>
              <Heading size='2xl'>
                {format(currentMonth, 'MMMM')}
                <Text as='span' color='fg.muted' ml={1}>
                  {format(currentMonth, 'yyyy')}
                </Text>
              </Heading>

              <Spacer />
              <Tooltip content='Previous Month' aria-label={'Previous Month'}>
                <Button
                  onClick={() => handleMonthChange('prev')}
                  variant='outline'
                  size='xs'
                  aria-label='Previous month'
                >
                  <Icon as={ChevronLeftIcon} w={4} h={4} />
                </Button>
              </Tooltip>
              <Tooltip content='Today' aria-label={'Today'}>
                <Button
                  variant='outline'
                  onClick={() => {
                    const today = new Date();
                    setCurrentMonth(today);
                    setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
                  }}
                  size='xs'
                >
                  Today
                </Button>
              </Tooltip>
              <Tooltip content='Next Month' aria-label={'Next Month'}>
                <Button
                  onClick={() => handleMonthChange('next')}
                  variant='outline'
                  size='xs'
                  aria-label='Next month'
                >
                  <Icon as={ChevronRightIcon} w={4} h={4} />
                </Button>
              </Tooltip>
            </Flex>
          </Flex>

          <Box mb={6}>
            {isLoading ? (
              <VStack gap={4}>
                <SimpleGrid columns={7} w='100%' gap={4}>
                  {Array(35)
                    .fill(0)
                    .map((_, i) => (
                      <Skeleton key={i} height='100px' />
                    ))}
                </SimpleGrid>
              </VStack>
            ) : (
              <Box>
                <SimpleGrid columns={7} mb={2} textAlign='center'>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <Box key={day} fontWeight='bold' py={2}>
                      {day}
                    </Box>
                  ))}
                </SimpleGrid>

                <SimpleGrid columns={7} gap={1}>
                  {calendarDays.map((day, index) => {
                    const dayEvents = day ? getEventsForDay(day) : [];
                    const isCurrentDay = day ? isToday(day) : false;
                    const isWeekend = day ? [0, 6].includes(getDay(day)) : false;

                    return (
                      <Box
                        key={index}
                        borderRadius='md'
                        border='1px solid'
                        borderColor='border'
                        height='100px'
                        overflow='auto'
                        position='relative'
                        opacity={day ? 1 : 0.3}
                        p={1}
                        cursor={day ? 'pointer' : 'default'}
                        bg={isWeekend ? 'bg.subtle' : 'bg.panel'}
                        onClick={() => {
                          if (day && isAdmin && !isCancelled) {
                            handleCreateEvent(day);
                          }
                        }}
                      >
                        {day && (
                          <>
                            <Flex justify='flex-end'>
                              <Badge
                                bg={isCurrentDay ? 'brand.500' : isWeekend ? 'bg.subtle' : 'bg.panel'}
                                color={isCurrentDay ? 'white' : isWeekend ? 'fg.muted' : 'fg'}
                                fontWeight='bold'
                                borderRadius={'full'}
                                fontSize='xs'
                              >
                                {format(day, 'd')}
                              </Badge>
                            </Flex>
                            <VStack align='stretch' gap={1} mt={1}>
                              {dayEvents.length > 0
                                ? dayEvents.map((event) => (
                                    <Box
                                      key={event.id}
                                      bg={'brand.100'}
                                      px={1}
                                      borderRadius='lg'
                                      fontSize='xs'
                                      fontWeight='medium'
                                      _hover={{ cursor: 'pointer', bg: 'brand.200' }}
                                      title={event.title}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleView(event);
                                      }}
                                    >
                                      <Text truncate>
                                        <Icon
                                          as={event.status === 'published' ? EyeIcon : EyeSlashIcon}
                                          w={3}
                                          h={3}
                                          mr={1}
                                        />
                                        {event.event_time && (
                                          <Text as={'span'} fontSize='label' fontWeight='bold' mr={1}>
                                            {format(
                                              new Date(`2000-01-01T${event.event_time}`),
                                              'h:mm a',
                                            )}
                                          </Text>
                                        )}
                                        {event.title}
                                      </Text>
                                    </Box>
                                  ))
                                : null}
                            </VStack>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>
            )}
          </Box>
        </Container>

        <EventModal
          isOpen={eventModalState.isOpen}
          onClose={closeEventModal}
          eventId={eventModalState.eventId}
          selectedDate={eventModalState.selectedDate}
          onSuccess={handleEventSuccess}
        />

        {referenceId && (
          <EventDrawer
            {...itemDrawer.props}
            referenceId={referenceId}
            reload={handleEventSuccess}
            onEdit={() => {
              itemDrawer.close();
              const selectedEvent = events.find((e) => e.reference_id === referenceId);
              if (selectedEvent) {
                handleEditEvent(selectedEvent.id);
              }
            }}
          />
        )}
      </Card.Body>
    </Card.Root>
  );
}
